import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatMinorAmount } from '@/lib/commerce-format';
import { type OrderTender, useGetOrderTendersQuery } from '@/store/api/commerceP2cApi';

export function PaymentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialOrderId = searchParams.get('orderId') ?? '';
  const [orderId, setOrderId] = useState(initialOrderId);
  const [activeOrderId, setActiveOrderId] = useState(initialOrderId);
  const [cursor, setCursor] = useState<string | undefined>();
  const [rows, setRows] = useState<OrderTender[]>([]);
  const {
    data: page,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetOrderTendersQuery({ orderId: activeOrderId, cursor, limit: 25 }, { skip: !activeOrderId });

  useEffect(() => {
    const nextOrderId = searchParams.get('orderId') ?? '';
    setOrderId(nextOrderId);
    setActiveOrderId(nextOrderId);
  }, [searchParams]);

  useEffect(() => {
    setRows([]);
    setCursor(undefined);
  }, [activeOrderId]);

  useEffect(() => {
    if (!page) return;
    setRows((previous) => {
      if (!cursor) return page.data;
      const seen = new Set(previous.map((row) => row.id));
      return [...previous, ...page.data.filter((row) => !seen.has(row.id))];
    });
  }, [page, cursor]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const next = orderId.trim();
    setRows([]);
    setCursor(undefined);
    setActiveOrderId(next);
    if (next) setSearchParams({ orderId: next });
    else setSearchParams({});
  }

  return (
    <div className="space-y-6">
      <header><h1 className="text-2xl font-bold">Payments</h1><p className="text-sm text-muted-foreground">Order tender records are redacted. Provider and card details are not displayed.</p></header>
      <form className="flex flex-wrap items-end gap-3" onSubmit={submit}>
        <div className="min-w-64 flex-1 space-y-2"><Label htmlFor="tender-order-id">Order ID</Label><Input id="tender-order-id" required value={orderId} onChange={(event) => setOrderId(event.target.value)} /></div>
        <Button type="submit" disabled={!orderId.trim()}>View tenders</Button>
      </form>
      {activeOrderId && <div className="space-y-4">
        <p className="text-sm text-muted-foreground">Tender records for order <Link className="font-medium text-primary underline-offset-4 hover:underline" to={`/orders/${encodeURIComponent(activeOrderId)}`}>{activeOrderId}</Link></p>
        <section aria-busy={isLoading || isFetching}>
          {isLoading && rows.length === 0 ? <LoadingRows label="Loading order tenders" columns={6} />
            : isError && rows.length === 0 ? <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">Could not load tender records. The order ID is retained. <Button variant="outline" onClick={() => void refetch()}>Try again</Button></div>
              : rows.length === 0 ? <EmptyState title="No tender records returned" description="The selected order has no visible tenders in this response." />
                : <div className="space-y-3">{isError && <div role="alert" className="text-sm text-destructive">Could not refresh tender records; loaded rows are retained. <Button variant="outline" onClick={() => void refetch()}>Try again</Button></div>}<div role="region" tabIndex={0} className="overflow-x-auto rounded-md border" aria-label="Order tender records"><table className="w-full min-w-[48rem] text-sm"><thead className="bg-muted/50 text-left"><tr><th scope="col" className="px-4 py-3">Tender ID</th><th scope="col" className="px-4 py-3">Method</th><th scope="col" className="px-4 py-3">Status</th><th scope="col" className="px-4 py-3 text-right">Amount</th><th scope="col" className="px-4 py-3 text-right">Refunded</th><th scope="col" className="px-4 py-3">Created</th></tr></thead><tbody>{rows.map((tender) => <tr key={tender.id} className="border-t"><td className="px-4 py-3">{tender.id}</td><td className="px-4 py-3">{tender.kind}</td><td className="px-4 py-3">{tender.status}</td><td className="px-4 py-3 text-right tabular-nums">{formatMinorAmount(tender.amount, tender.currency)}</td><td className="px-4 py-3 text-right tabular-nums">{formatMinorAmount(tender.refundedAmount, tender.currency)}</td><td className="px-4 py-3">{formatDate(tender.createdAt)}</td></tr>)}</tbody></table></div><p className="text-sm text-muted-foreground">Loaded {rows.length} tender records for order {activeOrderId}.</p>{page?.nextCursor && <Button variant="outline" disabled={isFetching} onClick={() => setCursor(page.nextCursor ?? undefined)}>{isFetching ? 'Loading…' : 'Load more tenders'}</Button>}</div>}
        </section>
      </div>}
    </div>
  );
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}
